<script setup lang="ts">
import { ref } from "vue";

import Button from "@/dsComponents/buttons/Button.vue";
import FullModal from "@/dsComponents/modals/FullModal.vue";

import PdfManager from "@/components/PdfManager.vue";

interface DocumentPreviewProps {
    path: string;
    open: boolean;
    noActions?: boolean;
    notFullWidth?: boolean;
}

const props = defineProps<DocumentPreviewProps>();

const emit = defineEmits<{
    (e: "close"): void;
    (e: "delete"): void;
    (e: "add", path: string): void;
    (e: "downloadcv"): void;
}>();

const pdfManager = ref<InstanceType<typeof PdfManager> | undefined>();
const resumeActions = (action: "in" | "out" | "download" | "delete" | "edit") => {
    if (action === "in") pdfManager.value?.zoomIn();
    if (action === "out") pdfManager.value?.zoomOut();
    if (action === "download") pdfManager.value?.downloadCV();
    if (action === "delete") pdfManager.value?.removePdf();
    if (action === "edit") pdfManager.value?.editPdf();
};

const closeDocument = () => {
    emit("close");
};
</script>
<template>
    <FullModal @close="closeDocument" v-if="open" :not-full-width="props.notFullWidth">
        <template #header-actions>
            <div class="header">
                <div class="actions" v-if="path">
                    <Button type="neutral" size="sm" variant="stroke" icon-only="download" @click="resumeActions('download')"></Button>
                    <template v-if="!noActions">
                        <Button type="neutral" size="sm" variant="stroke" icon-only="edit" @click="resumeActions('edit')"></Button>
                        <Button type="error" size="sm" variant="stroke" icon-only="trash" @click="resumeActions('delete')"></Button>
                    </template>
                </div>
            </div>
        </template>
        <div class="cv-informations">
            <PdfManager
                :path="path"
                ref="pdfManager"
                @delete="emit('delete')"
                @add="(path) => emit('add', path)"
                :showActions="false"
                :show-rules="!path"
            />
        </div>
    </FullModal>
</template>
<style lang="scss" scoped>
.header {
    width: 100%;
    color: $text-main-900;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .actions {
        gap: 12px;
        display: flex;
    }
}

.cv-informations {
    gap: 12px;
    height: 100%;
    display: flex;
    overflow: auto;
    padding: var(--spacing-5xl, 40px) var(--spacing-xl, 16px);
    align-items: center;
    flex-direction: column;
    justify-content: center;

    .PdfManager {
        width: 100%;
        max-width: 720px;
        overflow: unset;
    }

    :deep(.PdfManager .pdfComponent) {
        width: 100%;
        overflow-y: unset;

        canvas {
            width: 100% !important;
            height: auto !important;
        }
    }
}
</style>
